import { useEffect, useState } from 'react';
import { useLocation, Link } from 'react-router-dom';
import { 
  LayoutDashboard, 
  Users, 
  FileText, 
  MessageSquare,
  MessagesSquare,
  Mail, 
  Calendar,
  CalendarCheck, 
  FolderOpen,
  ScanLine,
  Database,
  Bot, 
  Settings,
  Building2,
  UserCog,
  Menu,
  X,
  CheckSquare,
  Tags,
  ListChecks,
  FileBox,
  BookUser,
  BarChart3,
  Clock,
  SlidersHorizontal,
  Wallet
} from 'lucide-react';
import { cn } from '@/utils/cn';
import { useScannerInbox } from '@/hooks/useScannerInbox';
import { useAuthStore } from '@/store/authStore';
import { useSettings } from '@/hooks/useSettings';
import { DigitalClock } from './DigitalClock';

const navigation = [
  { name: 'Dashboard', href: '/dashboard', icon: LayoutDashboard, current: false },
  { name: 'Clienti', href: '/clients', icon: Users, current: false },
  { name: 'Pratiche', href: '/practices', icon: FileText, current: false },
  { name: 'Preventivi e conti', href: '/preventivi-e-conti', icon: Wallet, current: false },
  { name: 'Attività', href: '/activities', icon: CheckSquare, current: false },
  { name: 'WhatsApp', href: '/whatsapp', icon: MessageSquare, current: false },
  { name: 'Email', href: '/email', icon: Mail, current: false },
  { name: 'Rubrica', href: '/contacts', icon: BookUser, current: false },
  { name: 'Messaggi', href: '/messaggi', icon: MessagesSquare, current: false },
  { name: 'Agenda', href: '/agenda', icon: CalendarCheck, current: false },
  { name: 'Calendario', href: '/calendar', icon: Calendar, current: false },
  { name: 'Scanner', href: '/scanner', icon: ScanLine, current: false },
  { name: 'Documenti', href: '/documents', icon: FolderOpen, current: false },
  { name: 'Backup & Restore', href: '/backup', icon: Database, current: false },
  { name: 'AI Assistant Pro', href: '/ai-assistant-pro', icon: Bot, current: false },
];

// Configuration links visible to all users
const configNavigation = [
  { name: 'Tipi Pratica', href: '/practice-types', icon: Tags },
  { name: 'Tipi Attività', href: '/activity-types', icon: ListChecks },
  { name: 'Moduli', href: '/modules', icon: FileBox },
];

// Admin-only navigation
const adminNavigation = [
  { name: 'Utenti', href: '/users', icon: UserCog },
  { name: 'Statistiche operatori', href: '/admin/operator-stats', icon: BarChart3 },
  { name: 'Timbrature', href: '/admin/timbrature', icon: Clock },
  { name: 'Impostazioni timbrature', href: '/admin/timbrature/settings', icon: SlidersHorizontal },
  { name: 'Messaggi interni (audit)', href: '/admin/staff-messaging', icon: MessagesSquare },
  { name: 'Impostazioni', href: '/settings', icon: Settings },
];

export default function Sidebar() {
  const location = useLocation();
  const { pendingCount } = useScannerInbox();
  const { user } = useAuthStore();
  const { settings, refreshKey, settingsKey } = useSettings();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [runtimeEnv, setRuntimeEnv] = useState<string | null>(null);

  useEffect(() => {
    fetch('/api/health')
      .then((r) => r.json())
      .then((data: { environment?: string }) => {
        if (data?.environment) setRuntimeEnv(data.environment);
      })
      .catch(() => undefined);
  }, []);

  // Force re-render when settings change
  const companyName = settings?.general?.companyName || 'Studio Gori';
  const timezone = settings?.general?.timezone || 'Europe/Rome';

  const SidebarContent = () => (
    <div className="flex grow flex-col gap-y-5 overflow-y-auto bg-white border-r border-gray-200 px-6 pb-4">
      <div className="flex h-24 shrink-0 items-start justify-between py-4">
        <div className="flex flex-col gap-2 w-full">
          <div className="flex items-center">
            <Building2 className="h-8 w-8 text-blue-600" />
            <span className="ml-2 text-xl font-bold text-gray-900">
              {companyName}
            </span>
            {runtimeEnv && runtimeEnv !== 'production' && (
              <span className="ml-2 rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-amber-800">
                {runtimeEnv === 'development' ? 'develop' : runtimeEnv}
              </span>
            )}
            {window.electronAppVersion && (
              <span className="ml-2 text-xs text-gray-400 font-normal self-end mb-0.5">
                v{window.electronAppVersion}
              </span>
            )}
          </div>
          <div className="ml-10">
            <DigitalClock timezone={timezone} />
          </div>
        </div>
        {/* Close button for mobile */}
        <button
          type="button"
          className="lg:hidden rounded-md p-2 text-gray-700 hover:bg-gray-100"
          onClick={() => setMobileMenuOpen(false)}
        >
          <X className="h-6 w-6" />
        </button>
      </div>

      <nav className="flex flex-1 flex-col">
        <ul className="flex flex-1 flex-col gap-y-7">
          <li>
            <ul className="-mx-2 space-y-1">
              {navigation.filter((item) => item.href !== '/preventivi-e-conti' || user?.canAccessQuotesAndAccounts).map((item) => {
                const isActive = location.pathname === item.href;
                return (
                  <li key={item.name}>
                    <Link
                      to={item.href}
                      className={cn(
                        'group flex gap-x-3 rounded-md p-2 text-sm leading-6 font-semibold transition-colors',
                        isActive
                          ? 'bg-blue-50 text-blue-600'
                          : 'text-gray-700 hover:text-blue-600 hover:bg-gray-50'
                      )}
                      onClick={() => setMobileMenuOpen(false)} // Close menu on mobile when link is clicked
                    >
                      <item.icon
                        className={cn(
                          'h-6 w-6 shrink-0',
                          isActive ? 'text-blue-600' : 'text-gray-400 group-hover:text-blue-600'
                        )}
                        aria-hidden="true"
                      />
                      <span className="flex-1">{item.name}</span>
                      {item.href === '/scanner' && pendingCount > 0 && (
                        <span className="ml-auto inline-flex min-w-5 items-center justify-center rounded-full bg-blue-600 px-1.5 text-xs font-semibold text-white">
                          {pendingCount > 99 ? '99+' : pendingCount}
                        </span>
                      )}
                    </Link>
                  </li>
                );
              })}
            </ul>
          </li>

          {/* Configuration section visible to all users */}
          <li>
            <div className="text-xs font-semibold leading-6 text-gray-400">
              Configurazione
            </div>
            <ul className="-mx-2 mt-2 space-y-1">
              {configNavigation.map((item) => {
                const isActive = location.pathname === item.href;
                return (
                  <li key={item.name}>
                    <Link
                      to={item.href}
                      className={cn(
                        'group flex gap-x-3 rounded-md p-2 text-sm leading-6 font-semibold transition-colors',
                        isActive
                          ? 'bg-blue-50 text-blue-600'
                          : 'text-gray-700 hover:text-blue-600 hover:bg-gray-50'
                      )}
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      <item.icon
                        className={cn(
                          'h-6 w-6 shrink-0',
                          isActive ? 'text-blue-600' : 'text-gray-400 group-hover:text-blue-600'
                        )}
                        aria-hidden="true"
                      />
                      {item.name}
                    </Link>
                  </li>
                );
              })}
            </ul>
          </li>

          {/* Administration section only for ADMIN users */}
          {user?.role === 'ADMIN' && (
            <li>
              <div className="text-xs font-semibold leading-6 text-gray-400">
                Amministrazione
              </div>
              <ul className="-mx-2 mt-2 space-y-1">
                {adminNavigation.map((item) => {
                  const isActive = location.pathname === item.href;
                  return (
                    <li key={item.name}>
                      <Link
                        to={item.href}
                        className={cn(
                          'group flex gap-x-3 rounded-md p-2 text-sm leading-6 font-semibold transition-colors',
                          isActive
                            ? 'bg-blue-50 text-blue-600'
                            : 'text-gray-700 hover:text-blue-600 hover:bg-gray-50'
                        )}
                        onClick={() => setMobileMenuOpen(false)}
                      >
                        <item.icon
                          className={cn(
                            'h-6 w-6 shrink-0',
                            isActive ? 'text-blue-600' : 'text-gray-400 group-hover:text-blue-600'
                          )}
                          aria-hidden="true"
                        />
                        {item.name}
                      </Link>
                    </li>
                  );
                })}
              </ul>
            </li>
          )}

          {user?.role === 'WHATSAPP_MANAGER' && (
            <li>
              <div className="text-xs font-semibold leading-6 text-gray-400">
                WhatsApp
              </div>
              <ul className="-mx-2 mt-2 space-y-1">
                <li>
                  <Link
                    to="/settings"
                    className={cn(
                      'group flex gap-x-3 rounded-md p-2 text-sm leading-6 font-semibold transition-colors',
                      location.pathname === '/settings'
                        ? 'bg-blue-50 text-blue-600'
                        : 'text-gray-700 hover:text-blue-600 hover:bg-gray-50'
                    )}
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    <Settings
                      className={cn(
                        'h-6 w-6 shrink-0',
                        location.pathname === '/settings'
                          ? 'text-blue-600'
                          : 'text-gray-400 group-hover:text-blue-600'
                      )}
                      aria-hidden="true"
                    />
                    Impostazioni WhatsApp
                  </Link>
                </li>
              </ul>
            </li>
          )}

          {/* User info */}
          <li className="mt-auto">
            <div className="p-3 bg-gray-50 rounded-lg">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <div className="h-8 w-8 rounded-full bg-blue-600 flex items-center justify-center">
                    <span className="text-sm font-medium text-white">
                      {user?.firstName?.[0] || user?.email?.[0]?.toUpperCase()}
                    </span>
                  </div>
                </div>
                <div className="ml-3 min-w-0 flex-1">
                  <p className="text-sm font-medium text-gray-900 truncate">
                    {user?.firstName && user?.lastName 
                      ? `${user.firstName} ${user.lastName}`
                      : user?.email
                    }
                  </p>
                  <p className="text-xs text-gray-500 truncate">
                    {user?.role === 'ADMIN' && 'Amministratore'}
                    {user?.role === 'WHATSAPP_MANAGER' && 'WhatsApp Manager'}
                    {user?.role === 'OPERATOR' && 'Operatore'}
                  </p>
                </div>
              </div>
            </div>
          </li>
        </ul>
      </nav>
    </div>
  );

  return (
    <>
      {/* Mobile menu button */}
      <div className="lg:hidden">
        <button
          type="button"
          className="fixed top-4 left-4 z-50 inline-flex items-center justify-center rounded-md bg-white p-2 text-gray-700 shadow-md hover:bg-gray-100 hover:text-gray-900"
          onClick={() => setMobileMenuOpen(true)}
        >
          <Menu className="h-6 w-6" />
        </button>
      </div>

      {/* Desktop sidebar */}
      <div className="hidden lg:fixed lg:inset-y-0 lg:z-50 lg:flex lg:w-64 lg:flex-col">
        <SidebarContent key={settingsKey} />
      </div>

      {/* Mobile sidebar */}
      {mobileMenuOpen && (
        <>
          {/* Background overlay */}
          <div
            className="fixed inset-0 z-40 bg-gray-600 bg-opacity-75 lg:hidden"
            onClick={() => setMobileMenuOpen(false)}
          />
          
          {/* Mobile sidebar panel */}
          <div className="fixed inset-y-0 left-0 z-50 w-64 lg:hidden">
            <SidebarContent key={settingsKey} />
          </div>
        </>
      )}
    </>
  );
}